import { useState, useEffect } from 'react';
import { ChefHat, Clock, Check } from 'lucide-react';
import { Button } from './ui/button';
import { cookwareLabel } from '../utils/cookwareLabels';

var cookwareOptions = [
  { id: 'oven', icon: '🔥' },
  { id: 'stovetop', icon: '🍳' },
  { id: 'air_fryer', icon: '🌀' },
  { id: 'instant_pot', icon: '⚡' },
  { id: 'microwave', icon: '📻' },
  { id: 'grill', icon: '🔥' },
  { id: 'blender', icon: '🥤' }
];

// Saved cookware is equipment the person owns. "no_cook" is a RECIPE method
// (equipment-free recipes are always eligible), never a saved cookware choice,
// so it is never emitted from here even if an old value is passed in.
function equipmentOnly(list) {
  return (list || []).filter(function(id) { return id !== 'no_cook'; });
}

var cookTimeOptions = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60+ min' }
];

/**
 * CookingPreferencesSelector - Component for selecting cooking preferences
 * 
 * Two usage modes:
 * 1. Controlled mode (from settings/onboarding):
 *    - onCookwareChange: function to update cookware selection live
 *    - onCookTimeChange: function to update cook time live
 *    - showDoneButton + onDone: optional Done button callback
 * 
 * 2. Save mode (from Home.jsx regenerate modal):
 *    - onSave(cookware, cookTime): function called when user saves (passes final values)
 *    - onBack: function to go back without saving
 * 
 * Props:
 * - selectedCookware: array of selected cookware IDs
 * - selectedCookTime: number (15, 30, 45, or 60)
 * - onCookwareChange: function to update cookware selection (controlled mode)
 * - onCookTimeChange: function to update cook time (controlled mode)
 * - onSave: function(cookware, cookTime) called when user saves (save mode)
 * - onBack: function to go back (save mode)
 * - onDone: function called when user clicks Done (controlled mode)
 * - showDoneButton: boolean - show the Done button
 * - compact: boolean - use compact layout
 */
function CookingPreferencesSelector({ 
  selectedCookware: propCookware = [], 
  onCookwareChange,
  selectedCookTime: propCookTime = null,
  onCookTimeChange,
  onSave,
  onBack,
  onDone,
  showDoneButton = true,
  compact = false
}) {
  // Use internal state for save mode (when onSave is provided)
  var [localCookware, setLocalCookware] = useState(propCookware);
  var [localCookTime, setLocalCookTime] = useState(propCookTime);
  
  // Sync with props when they change
  useEffect(function() {
    setLocalCookware(propCookware);
  }, [propCookware]);
  
  useEffect(function() {
    setLocalCookTime(propCookTime);
  }, [propCookTime]);
  
  // Determine if we're in save mode (onSave provided) or controlled mode
  var isSaveMode = typeof onSave === 'function';
  
  // Get current values (use local state in save mode, props in controlled mode)
  var selectedCookware = isSaveMode ? localCookware : propCookware;
  var selectedCookTime = isSaveMode ? localCookTime : propCookTime;
  
  function toggleCookware(id) {
    var current = equipmentOnly(selectedCookware);
    var newCookware;
    if (current.includes(id)) {
      newCookware = current.filter(function(c) { return c !== id; });
    } else {
      newCookware = current.concat([id]);
    }
    
    if (isSaveMode) {
      setLocalCookware(newCookware);
    } else if (onCookwareChange) {
      onCookwareChange(newCookware);
    }
  }
  
  function handleCookTimeSelect(value) {
    if (isSaveMode) {
      setLocalCookTime(value);
    } else if (onCookTimeChange) {
      onCookTimeChange(value);
    }
  }
  
  function handleSave() {
    if (onSave) {
      onSave(equipmentOnly(localCookware), localCookTime);
    }
  }

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {/* Cookware Selection */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ChefHat size={18} className="text-accent" />
          <h3 className="font-heading font-semibold text-sm">Available Cookware</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {cookwareOptions.map(function(option) {
            var isSelected = selectedCookware.includes(option.id);
            return (
              <button
                key={option.id}
                onClick={function() { toggleCookware(option.id); }}
                className={'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ' + (
                  isSelected
                    ? 'bg-accent text-white'
                    : 'bg-secondary/50 hover:bg-secondary text-foreground'
                )}
                data-testid={'cookware-' + option.id}
              >
                {isSelected && <Check size={14} />}
                <span>{option.icon}</span>
                <span>{cookwareLabel(option.id)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cook Time Selection */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Clock size={18} className="text-accent" />
          <h3 className="font-heading font-semibold text-sm">Max Cooking Time</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {cookTimeOptions.map(function(option) {
            var isSelected = selectedCookTime === option.value;
            return (
              <button
                key={option.value}
                onClick={function() { handleCookTimeSelect(option.value); }}
                className={'px-4 py-2 rounded-lg text-sm font-medium transition-all ' + (
                  isSelected
                    ? 'bg-accent text-white'
                    : 'bg-secondary/50 hover:bg-secondary text-foreground'
                )}
                data-testid={'cooktime-' + option.value}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Save Mode Buttons (from Home.jsx regenerate modal) */}
      {isSaveMode && (
        <div className="space-y-3 pt-4">
          <Button
            onClick={handleSave}
            className="w-full h-11 rounded-xl bg-accent text-white font-semibold"
            data-testid="cooking-prefs-save-btn"
          >
            Save Preferences
          </Button>
          {onBack && (
            <Button
              onClick={onBack}
              variant="ghost"
              className="w-full h-10 rounded-xl text-muted-foreground"
              data-testid="cooking-prefs-back-btn"
            >
              Back
            </Button>
          )}
        </div>
      )}
      
      {/* Done Button (controlled mode) */}
      {!isSaveMode && showDoneButton && onDone && (
        <Button
          onClick={onDone}
          className="w-full h-10 rounded-lg bg-accent text-white font-medium"
          data-testid="cooking-prefs-done-btn"
        >
          Done
        </Button>
      )}
    </div>
  );
}

export default CookingPreferencesSelector;
